import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type QuickOffset = {
  label: string;
  minutes: number;
};

type CalendarDay = {
  date: Date;
  label: number;
  inCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
};

type TimeOption = {
  label: string;
  value: number;
};

type TimeColumnType = 'hour' | 'minute';

@Component({
  standalone: false,
  selector: 'admin-date-time-picker',
  templateUrl: './admin-date-time-picker.component.html',
  styleUrls: ['./admin-date-time-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdminDateTimePickerComponent),
      multi: true,
    },
  ],
})
export class AdminDateTimePickerComponent implements ControlValueAccessor {
  @ViewChild('pickerTrigger') pickerTrigger?: ElementRef<HTMLButtonElement>;
  @Input() format = 'MM/dd/yyyy HH:mm';
  @Input() placeholder = 'Select date and time';
  @Input() ariaLabel = 'Select date and time';
  @Input() allowClear = true;
  @Input() showQuickOffsets = false;
  @Input() showTime = true;
  @Input() locale = 'en-US';
  @Input() dateLabel = 'Select date';
  @Input() timeLabel = 'Select time';
  @Input() previousMonthLabel = 'Previous month';
  @Input() nextMonthLabel = 'Next month';
  @Input() clearLabel = 'Clear selected date and time';
  @Input() quickOffsets: QuickOffset[] = [
    { label: 'Now', minutes: 0 },
    { label: '+15 min', minutes: 15 },
    { label: '+30 min', minutes: 30 },
    { label: '+1 hour', minutes: 60 },
  ];

  value: Date | null = null;
  draftValue: Date = this.roundToMinute(new Date());
  visibleMonth = new Date();
  isOpen = false;
  disabled = false;
  readonly hourOptions: TimeOption[] = Array.from({ length: 24 }, (_, value) => ({
    label: this.pad(value),
    value,
  }));
  readonly minuteOptions: TimeOption[] = Array.from({ length: 60 }, (_, value) => ({
    label: this.pad(value),
    value,
  }));

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (!this.isOpen) return;
    event.preventDefault();
    this.close();
    this.pickerTrigger?.nativeElement.focus();
  }

  writeValue(value: Date | string | null | undefined): void {
    const nextTime = value == null || value === '' ? null : new Date(value).getTime();
    if ((this.value?.getTime() ?? null) === nextTime) return;
    if (value == null || value === '') {
      this.value = null;
      this.draftValue = this.roundToMinute(new Date());
      return;
    }

    const parsed = value instanceof Date ? value : new Date(value);
    this.value = Number.isNaN(parsed.getTime()) ? null : parsed;
    this.draftValue = this.value ? new Date(this.value) : this.roundToMinute(new Date());
    this.visibleMonth = new Date(
      this.draftValue.getFullYear(),
      this.draftValue.getMonth(),
      1,
    );
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  openPicker(event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.disabled) return;

    this.draftValue = this.value
      ? new Date(this.value)
      : this.roundToMinute(new Date());
    this.visibleMonth = new Date(
      this.draftValue.getFullYear(),
      this.draftValue.getMonth(),
      1,
    );
    this.isOpen = true;
    window.setTimeout(() => this.scrollSelectedTimeIntoView());
  }

  setValue(value: Date | null): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.setValue(null);
    this.isOpen = false;
  }

  close(): void {
    this.isOpen = false;
    this.onTouched();
  }

  applyOffset(minutes: number): void {
    const next = new Date(Date.now() + minutes * 60 * 1000);
    next.setSeconds(0, 0);
    this.draftValue = next;
    this.visibleMonth = new Date(next.getFullYear(), next.getMonth(), 1);
    this.commitDraft();
    window.setTimeout(() => this.scrollSelectedTimeIntoView());
  }

  previousMonth(): void {
    this.visibleMonth = new Date(
      this.visibleMonth.getFullYear(),
      this.visibleMonth.getMonth() - 1,
      1,
    );
  }

  nextMonth(): void {
    this.visibleMonth = new Date(
      this.visibleMonth.getFullYear(),
      this.visibleMonth.getMonth() + 1,
      1,
    );
  }

  selectDate(day: CalendarDay): void {
    const next = new Date(this.draftValue);
    next.setFullYear(day.date.getFullYear(), day.date.getMonth(), day.date.getDate());
    this.draftValue = next;
    this.visibleMonth = new Date(day.date.getFullYear(), day.date.getMonth(), 1);
    this.commitDraft();
    if (!this.showTime) {
      this.close();
      this.pickerTrigger?.nativeElement.focus();
      return;
    }
    window.setTimeout(() => this.scrollSelectedTimeIntoView());
  }

  adjustHour(delta: number): void {
    const next = new Date(this.draftValue);
    next.setHours((next.getHours() + delta + 24) % 24);
    this.draftValue = next;
    this.commitDraft();
  }

  adjustMinute(delta: number): void {
    const next = new Date(this.draftValue);
    next.setMinutes((next.getMinutes() + delta + 60) % 60);
    this.draftValue = next;
    this.commitDraft();
  }

  setHour(value: string | number, shouldCenter = true): void {
    const hour = Number(value);
    if (Number.isNaN(hour)) return;
    const next = new Date(this.draftValue);
    next.setHours(this.clamp(hour, 0, 23));
    this.draftValue = next;
    this.commitDraft();
    if (shouldCenter) {
      window.setTimeout(() => this.scrollSelectedTimeIntoView());
    }
  }

  setMinute(value: string | number, shouldCenter = true): void {
    const minute = Number(value);
    if (Number.isNaN(minute)) return;
    const next = new Date(this.draftValue);
    next.setMinutes(this.clamp(minute, 0, 59));
    this.draftValue = next;
    this.commitDraft();
    if (shouldCenter) {
      window.setTimeout(() => this.scrollSelectedTimeIntoView());
    }
  }

  onTimeColumnScroll(event: Event, type: TimeColumnType): void {
    const container = event.target as HTMLElement;
    const nextValue = this.findCenteredTimeValue(container);
    if (nextValue == null) return;

    if (type === 'hour' && nextValue !== this.draftValue.getHours()) {
      this.setHour(nextValue, false);
    }

    if (type === 'minute' && nextValue !== this.draftValue.getMinutes()) {
      this.setMinute(nextValue, false);
    }
  }

  get calendarDays(): CalendarDay[] {
    const year = this.visibleMonth.getFullYear();
    const month = this.visibleMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const start = new Date(year, month, 1 - firstDay.getDay());
    const today = new Date();

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return {
        date,
        label: date.getDate(),
        inCurrentMonth: date.getMonth() === month,
        isToday: this.isSameDate(date, today),
        isSelected: this.isSameDate(date, this.draftValue),
      };
    });
  }

  get monthLabel(): string {
    return this.visibleMonth.toLocaleDateString(this.locale, {
      month: 'long',
      year: 'numeric',
    });
  }

  get weekdays(): string[] {
    const formatter = new Intl.DateTimeFormat(this.locale, { weekday: 'narrow' });
    return Array.from({ length: 7 }, (_, index) => formatter.format(new Date(2024, 0, 7 + index)));
  }

  get displayValue(): string {
    return this.value ? this.formatDate(this.value) : '';
  }

  get selectedHour(): string {
    return this.pad(this.draftValue.getHours());
  }

  get selectedMinute(): string {
    return this.pad(this.draftValue.getMinutes());
  }

  get previewDate(): string {
    return this.draftValue.toLocaleDateString(this.locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }

  private formatDate(date: Date): string {
    return this.format
      .replace('yyyy', `${date.getFullYear()}`)
      .replace('MM', this.pad(date.getMonth() + 1))
      .replace('dd', this.pad(date.getDate()))
      .replace('HH', this.pad(date.getHours()))
      .replace('hh', this.pad(date.getHours()))
      .replace('mm', this.pad(date.getMinutes()));
  }

  private isSameDate(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private roundToMinute(date: Date): Date {
    const next = new Date(date);
    next.setSeconds(0, 0);
    return next;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private commitDraft(): void {
    this.setValue(new Date(this.draftValue));
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private findCenteredTimeValue(container: HTMLElement): number | null {
    const options = Array.from(
      container.querySelectorAll<HTMLElement>('.time-scroll-option'),
    );
    if (options.length === 0) return null;

    const containerRect = container.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;
    let closestOption: HTMLElement | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const option of options) {
      const optionRect = option.getBoundingClientRect();
      const optionCenterY = optionRect.top + optionRect.height / 2;
      const distance = Math.abs(optionCenterY - centerY);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestOption = option;
      }
    }

    const rawValue = closestOption?.dataset['value'];
    if (rawValue == null) return null;

    const value = Number(rawValue);
    return Number.isNaN(value) ? null : value;
  }

  private scrollSelectedTimeIntoView(): void {
    const selectedOptions = this.elementRef.nativeElement.querySelectorAll(
      '.time-scroll-option.selected',
    );
    selectedOptions.forEach((option) => {
      option.scrollIntoView({ block: 'center' });
    });
  }
}
